import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Colors, Text, Button, Link, inEmbedMode } from "../../odc-ui";
import { ErrorModalDialog } from "../../components/uikit";
import useCurrentTopUpCampaign from "../../hooks/use_current_top_up_campaign";
import useCurrentVoucherCampaign from "../../hooks/use_current_voucher_campaign";
import { Frame } from "@bitesite/react-figstrap";
import SquareCardFields from "../../components/square_cards/square_card_fields";
import { tokenizeSquareCard } from "../../utilities/square_helpers";
import TreatmentCatgorySelection from "./common/treatment_category_selection";
import ProductSelection from "./common/product_selection";
import BooleanSelection from "./common/boolean_selection";
import UserFields from "./common/user_fields";
import ConfirmationModal from "./prepaid/confirmation_modal";
import ExternalPurchaseSuccess from "./common/external_purchase_success";

/* 2 modes: 
   - Parent controls treatmentCategory: Pass in initialTreatmentCategory and hideTreatmentCategorySelector = true
   - This component controls treatmentCategory: Don't pass in initialTreatmentCategory and hideTreatmentCategorySelector = false
*/
const AddFundsPrepaid = ({
  initialTreatmentCategory,
  hideTreatmentCategorySelector,
  renderPreamble,
  external,
  theme,
}) => {
  const authenticated = window.gon.current_user;

  const [treatmentCategory, setTreatmentCategory] = useState(
    initialTreatmentCategory
  );

  const [showExternalPurchaseSuccess, setShowExternalPurchaseSuccess] =
    useState(false);
  const [submittingForm, setSubmittingForm] = useState(false);
  const [squareCard, setSquareCard] = useState();
  const [cardBrand, setCardBrand] = useState("");

  const [selectedProduct, setSelectedProduct] = useState();

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [showTopUp, setShowTopUp] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState();
  const [showVoucher, setShowVoucher] = useState(false);
  const [voucherDescription, setVoucherDescription] = useState();

  /* For unauthenticated flow */
  const [hasExistingAccount, setHasExistingAccount] = useState();
  const [firstName, setFirstName] = useState();
  const [lastName, setLastName] = useState();
  const [email, setEmail] = useState();
  const [phone, setPhone] = useState();
  const [selectedDateOfBirthMonthOption, setSelectedDateOfBirthMonthOption] =
    useState({});
  const [selectedDateOfBirthDayOption, setSelectedDateOfBirthDayOption] =
    useState({});

  const { currentTopUpCampaign } = useCurrentTopUpCampaign(
    true,
    treatmentCategory,
    "prepaid"
  );
  const { currentVoucherCampaign } = useCurrentVoucherCampaign(
    true,
    treatmentCategory,
    "prepaid"
  );

  const createPaymentForUnauthenticatedUser = async (token) => {
    $.ajax({
      url: "/orders/create_for_unauthenticated_user",
      method: "POST",
      data: {
        source_id: token,
        square_item_id: selectedProduct.square_item_id,
        square_item_variation_id: selectedProduct.square_item_variation_id,
        treatment_category: treatmentCategory,
        user: {
          email,
          profile_attributes: {
            first_name: firstName,
            last_name: lastName,
            phone,
            date_of_birth_month: selectedDateOfBirthMonthOption.value,
            date_of_birth_day: selectedDateOfBirthDayOption.value,
          },
        },
        has_existing_account: hasExistingAccount,
      },
      success: () => {
        setShowExternalPurchaseSuccess(true);
        setSubmittingForm(false);
      },
      error: (jqXHR) => {
        if (jqXHR.responseJSON && jqXHR.responseJSON.errors.length > 0) {
          setErrorMessage(jqXHR.responseJSON.errors[0]);
        }
        setSubmittingForm(false);
        setShowError(true);
        setShowConfirmation(false);
      },
    });
  };

  const createPaymentForAuthenticatedUser = async (token) => {
    $.ajax({
      url: "/orders",
      method: "POST",
      data: {
        source_id: token,
        square_item_id: selectedProduct.square_item_id,
        square_item_variation_id: selectedProduct.square_item_variation_id,
        treatment_category: treatmentCategory,
      },
      success: () => {
        setSubmittingForm(false);
        if (external) {
          setShowExternalPurchaseSuccess(true);
        } else {
          window.location.href = "/?funds_added=true";
        }
      },
      error: (jqXHR) => {
        if (jqXHR.responseJSON && jqXHR.responseJSON.errors.length > 0) {
          setErrorMessage(jqXHR.responseJSON.errors[0]);
        }
        setSubmittingForm(false);
        setShowError(true);
        setShowConfirmation(false);
      },
    });
  };

  const handleConfirmPurchase = async (event) => {
    event.preventDefault();

    if (!submittingForm) {
      try {
        // disable the submit button as we await tokenization and make a
        // payment request.
        setSubmittingForm(true);
        const token = await tokenizeSquareCard(squareCard);

        if (authenticated) {
          createPaymentForAuthenticatedUser(token);
        } else {
          createPaymentForUnauthenticatedUser(token);
        }
      } catch (e) {
        setSubmittingForm(false);
        setShowConfirmation(false);
        setErrorMessage(e.message);
        setShowError(true);
      }
    }
  };

  const handleAddFunds = () => {
    if (!selectedProduct) {
      window.alert("Please select an amount.");
    } else {
      setShowConfirmation(true);
    }
  };

  const showSubmitButton = () => {
    if (authenticated) {
      return selectedProduct;
    } else {
      return selectedProduct && hasExistingAccount !== undefined;
    }
  };

  const determineTopUp = () => {
    if (currentTopUpCampaign && currentTopUpCampaign.id) {
      const topUpCampaignItemForSelectedProduct =
        currentTopUpCampaign.top_up_campaign_items.find((item) => {
          return (
            item.square_item_variation_id ===
            selectedProduct.square_item_variation_id
          );
        });

      if (topUpCampaignItemForSelectedProduct) {
        setShowTopUp(true);
        setTopUpAmount(
          topUpCampaignItemForSelectedProduct.amount_in_hundredths / 100
        );
      } else {
        setShowTopUp(false);
      }
    }
  };

  const determineVoucher = () => {
    if (currentVoucherCampaign && currentVoucherCampaign.id) {
      const voucherCampaignItemForSelectedProduct =
        currentVoucherCampaign.voucher_campaign_items.find((item) => {
          return (
            item.square_item_variation_id ===
            selectedProduct.square_item_variation_id
          );
        });

      if (voucherCampaignItemForSelectedProduct) {
        setShowVoucher(true);
        setVoucherDescription(
          voucherCampaignItemForSelectedProduct.description
        );
      } else {
        setShowVoucher(false);
      }
    }
  };

  useEffect(() => {
    if (selectedProduct) {
      determineTopUp();
      determineVoucher();
    }
  }, [selectedProduct]);

  useEffect(() => {
    if (hideTreatmentCategorySelector) {
      setTreatmentCategory(initialTreatmentCategory);
    }
  }, [initialTreatmentCategory]);

  if (showExternalPurchaseSuccess) {
    return (
      <ExternalPurchaseSuccess
        hasExistingAccount={hasExistingAccount}
        message="Your order has been successfully placed."
        detailsUrl="/dashboard"
        newAccountMessage="You have also been registered for a Healthcoin® Rewards account to manage your funds. You will receive an email with further instructions."
      />
    );
  }

  return (
    <div className="component-add-funds-prepaid">
      {renderPreamble && renderPreamble()}
      <Frame vertical gap={40}>
        {!hideTreatmentCategorySelector && (
          <TreatmentCatgorySelection
            caption="Which type of treatment would you like to purchase Healthcoin® Prepaid for?"
            treatmentCategory={treatmentCategory}
            onTreatmentCategoryClick={setTreatmentCategory}
            theme={theme}
          />
        )}

        {treatmentCategory && (
          <Frame vertical gap={20}>
            <ProductSelection
              purchaseMode="prepaid"
              treatmentCategory={treatmentCategory}
              theme={theme}
              selectedProduct={selectedProduct}
              onProductClick={setSelectedProduct}
            />
            {showTopUp && (
              <div>
                You&apos;ll receive a top-up of{" "}
                <Text color={Colors.gold200}>
                  {topUpAmount} Healthcoin<sup>®</sup>
                </Text>{" "}
                for adding this amount!
              </div>
            )}
            {showVoucher && (
              <div>
                Plus receive{" "}
                <Text color={Colors.gold200}>{voucherDescription}</Text>!
              </div>
            )}
          </Frame>
        )}

        {selectedProduct && (
          <form id="payment-form">
            <Frame vertical className="fgs-al fgs-al-g-40">
              <div>
                <div className="payment-label">
                  Enter Payment Card Information
                </div>

                <SquareCardFields
                  onChange={(newSquareCard, newCardBrand) => {
                    setSquareCard(newSquareCard);
                    setCardBrand(newCardBrand);
                  }}
                />
              </div>
            </Frame>
          </form>
        )}

        {!authenticated && selectedProduct && (
          <BooleanSelection
            question={
              <>
                Heathcoin® Prepaid purchases are set up through{" "}
                <Link
                  type={inEmbedMode() ? "embed" : ""}
                  href="/"
                  target="_blank"
                >
                  Healthcoin®
                </Link>
                . Do you already have a Healthcoin® account?
              </>
            }
            selectedValue={hasExistingAccount}
            onOptionClick={setHasExistingAccount}
            theme={theme}
          />
        )}

        {hasExistingAccount !== undefined && !authenticated && (
          <UserFields
            prompt={
              hasExistingAccount ? (
                <>
                  Please enter the email address associated with your{" "}
                  <Link
                    type={inEmbedMode() ? "embed" : ""}
                    href="/"
                    target="_blank"
                  >
                    Healthcoin®
                  </Link>{" "}
                  account
                </>
              ) : (
                "Please enter your information"
              )
            }
            enabledFields={
              hasExistingAccount
                ? ["email"]
                : ["firstName", "lastName", "email", "phone", "dateOfBirth"]
            }
            firstName={firstName}
            lastName={lastName}
            email={email}
            phone={phone}
            onFirstNameChange={setFirstName}
            onLastNameChange={setLastName}
            onEmailChange={setEmail}
            onPhoneChange={setPhone}
            selectedDateOfBirthMonthOption={selectedDateOfBirthMonthOption}
            onSelectedDateOfBirthMonthOptionChange={
              setSelectedDateOfBirthMonthOption
            }
            selectedDateOfBirthDayOption={selectedDateOfBirthDayOption}
            onSelectedDateOfBirthDayOptionChange={
              setSelectedDateOfBirthDayOption
            }
          />
        )}

        {showSubmitButton() && (
          <Button
            type={
              inEmbedMode()
                ? "embed"
                : theme === "paige"
                ? "dark-paige"
                : "dark"
            }
            disabled={submittingForm}
            onClick={handleAddFunds}
            textTransform="none"
          >
            Add Funds
          </Button>
        )}
      </Frame>

      {showConfirmation && (
        <ConfirmationModal
          authenticated={authenticated}
          hasExistingAccount={hasExistingAccount}
          selectedProduct={selectedProduct}
          cardBrand={cardBrand}
          firstName={firstName}
          lastName={lastName}
          email={email}
          phone={phone}
          selectedDateOfBirthMonthOption={selectedDateOfBirthMonthOption}
          selectedDateOfBirthDayOption={selectedDateOfBirthDayOption}
          onSubmitClick={handleConfirmPurchase}
          submittingForm={submittingForm}
          theme={theme}
          onCloseClick={() => setShowConfirmation(false)}
        />
      )}
      {showError && (
        <ErrorModalDialog
          headerText="Payment Failed"
          messageText="We're sorry, but there was an error processing your payment. Please check your card details, and try again."
          submessageText={errorMessage}
          renderActions={() => (
            <a
              className="btn btn-system-alert"
              onClick={(e) => {
                e.preventDefault();
                setShowError(false);
              }}
            >
              Try Again
            </a>
          )}
          onClose={() => setShowError(false)}
        />
      )}
    </div>
  );
};

AddFundsPrepaid.propTypes = {
  initialTreatmentCategory: PropTypes.string,
  hideTreatmentCategorySelector: PropTypes.bool,
  external: PropTypes.bool,
  renderPreamble: PropTypes.func,
  theme: PropTypes.string,
};

export default AddFundsPrepaid;
